import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AsamcoUserService, Permissions } from '@asamco/base';
import { BehaviorSubject, from, merge, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

type AdminTabId =
	| 'durable-function-app-management'
	| 'function-apps'
	| 'company-management'
	| 'user-company-management'
	| 'company-request-management'
	| 'users'
	| 'groups'
	| 'company-access-request-management'
	| 'link-overview'
	| 'audit-logs'
	| 'financial-integration-issues'
	| 'scheduler-data'
	| 'maintenance'
	| 'applications'
	| 'application-logs'
	| 'link-transfer'
	| 'mock-data'
	| 'warning-modal-messages'
	| 'joblog-pattern-matcher'
	| 'changelog-management'
	| 'ticket-integration'
	| 'integrationtester';

const DEFAULT_TAB: AdminTabId = 'company-management';

type PermissionKey = keyof typeof Permissions;

type TabInfo = { id: AdminTabId; text: string; icon: string; permissions: PermissionKey[] };
type TabGroup = { title: string; tabs: TabInfo[] };

@Component({
	selector: 'app-shell-admin-tooling-overview',
	templateUrl: './admin-tooling-overview.component.html',
	styleUrls: ['./admin-tooling-overview.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, NgFor, AsyncPipe, RouterOutlet],
})
export class AdminToolingOverviewComponent implements OnInit {
	private readonly allTabGroups: TabGroup[] = [
		{
			title: 'Company administration',
			tabs: [
				{ id: 'company-management', text: 'Companies', icon: 'far fa-hospitals', permissions: ['AL3_COMPANY_VIEW'] },
				{
					id: 'user-company-management',
					text: 'User companies',
					icon: 'far fa-hospital-user',
					permissions: ['AL3_USERCOMPANY_VIEW'],
				},
				{
					id: 'company-request-management',
					text: 'Sub-company requests',
					icon: 'far fa-clipboard-list',
					permissions: ['AL3_COMPANY_REQUESTS_HANDLE'],
				},
				{
					id: 'company-access-request-management',
					text: 'User access requests',
					icon: 'far fa-link',
					permissions: ['AL3_COMPANY_ACCESS_REQUESTS_HANDLE'],
				},
			],
		},
		{
			title: 'User management',
			tabs: [
				{ id: 'users', text: 'Users', icon: 'far fa-user', permissions: ['AS_UM_MI_USERS'] },
				{ id: 'groups', text: 'Groups', icon: 'far fa-users', permissions: ['AS_UM_MI_GROUPS'] },
			],
		},
		{
			title: 'Link operations',
			tabs: [
				{ id: 'link-overview', text: 'Link overview', icon: 'far fa-link', permissions: ['AL3_ADMIN_LINK_OVERVIEW'] },
				{ id: 'audit-logs', text: 'Audit logs', icon: 'far fa-pencil', permissions: ['AL3_ADMIN_LINK_OVERVIEW'] },
				{
					id: 'financial-integration-issues',
					text: 'Financial integration issues',
					icon: 'far fa-pencil',
					permissions: ['AL3_ADMIN_LINK_OVERVIEW'],
				},
				{ id: 'scheduler-data', text: 'Scheduler data', icon: 'far fa-pencil', permissions: ['AL3_ADMIN_LINK_OVERVIEW'] },
				{ id: 'maintenance', text: 'Maintenance / queue blocker', icon: 'far fa-wrench', permissions: ['AL3_MAINTENANCE_VIEW'] },
			],
		},
		{
			title: 'Applications and logs',
			tabs: [
				{ id: 'applications', text: 'Applications', icon: 'far fa-users-class', permissions: ['AL3_APPLICATION_MANAGEMENT'] },
				{ id: 'application-logs', text: 'Application Logs', icon: 'far fa-list', permissions: ['AP_VIEW_APPLICATION_LOGS'] },
			],
		},
		{
			title: 'Tools',
			tabs: [
				{ id: 'link-transfer', text: 'Link Transfer', icon: 'far fa-file-export', permissions: ['AL3_LINK_TRANSFER'] },
				{ id: 'mock-data', text: 'Mock Data', icon: 'far fa-file-export', permissions: ['AL3_MOCK_DATA'] },
				{
					id: 'warning-modal-messages',
					text: 'Warning modal messages',
					icon: 'far fa-exclamation-triangle',
					permissions: ['AL3_WARNING_MODAL_MESSAGES'],
				},
				{
					id: 'joblog-pattern-matcher',
					text: 'Job Log Pattern Matcher',
					icon: 'far fa-file-export',
					permissions: ['AL3_JOBLOG_PATTERN_MATCHER'],
				},
				{ id: 'changelog-management', text: 'Changelog', icon: 'far fa-newspaper', permissions: ['AL3_CHANGELOG'] },
			],
		},
		{
			title: 'Super admin',
			tabs: [
				{
					id: 'durable-function-app-management',
					text: 'Durable function apps',
					icon: 'far fa-wrench',
					permissions: ['AL3_FUNC_APP'],
				},
				{ id: 'function-apps', text: 'Function app versioning', icon: 'far fa-cog', permissions: ['AL3_FUNC_APP'] },
				{ id: 'ticket-integration', text: 'Ticket integration', icon: 'far fa-comments', permissions: ['AL3_TICKET_INTEGRATION'] },
				{ id: 'integrationtester', text: 'Integration tester', icon: 'far fa-play', permissions: ['AL3_INT_TEST'] },
			],
		},
	];

	public readonly tabGroups$ = from(this.buildTabGroups());
	public navCollapsed = false;

	// ALTabs expects a subject-like thing it can .next(tab.id) into
	public readonly selectedTabId$ = new BehaviorSubject<AdminTabId | null>(null);

	constructor(private asamcoUserService: AsamcoUserService, private router: Router, private activatedRoute: ActivatedRoute) {}

	ngOnInit(): void {
		this.tabGroups$
			.pipe(
				switchMap(tabGroups => {
					const tabInfos = this.flattenTabGroups(tabGroups);
					return merge(of(null), this.router.events.pipe(filter(e => e instanceof NavigationEnd))).pipe(
						map(() => {
							const currentTab = this.activatedRoute.firstChild?.snapshot.url?.[0]?.path as AdminTabId | undefined;
							return tabInfos.some(tab => tab.id === currentTab) ? currentTab : null;
						}),
						distinctUntilChanged(),
						map(tab => ({ tab }))
					);
				})
			)
			.subscribe(({ tab }) => {
				this.selectedTabId$.next(tab);
				if (tab && (!this.activatedRoute.firstChild || tab !== this.activatedRoute.firstChild.snapshot.url?.[0]?.path)) {
					this.router.navigate([tab], { relativeTo: this.activatedRoute });
				}
			});
	}

	onTabClick = (tab: TabInfo) => {
		this.selectedTabId$.next(tab.id);
		this.router.navigate([tab.id], { relativeTo: this.activatedRoute });
	};

	public toggleNav(): void {
		this.navCollapsed = !this.navCollapsed;
	}

	private async buildTabGroups(): Promise<TabGroup[]> {
		const assignedPermissions = await this.asamcoUserService.getPermissions();

		return this.allTabGroups
			.map(group => ({
				...group,
				tabs: group.tabs.filter(tab =>
					tab.permissions.some(permission => assignedPermissions.includes(Permissions[permission].toLowerCase()))
				),
			}))
			.filter(group => group.tabs.length > 0);
	}

	private flattenTabGroups(tabGroups: TabGroup[]): TabInfo[] {
		return tabGroups.reduce<TabInfo[]>((tabs, group) => tabs.concat(group.tabs), []);
	}
}
